// Floating help bubbles linking to docs.

import { CloseLarge } from '@carbon/icons-react';
import { Image, Pressable, StyleSheet, View } from 'react-native';
import type { Datastore } from 'util/datastore';
import { useDatastore, useModuleObject } from 'util/datastore';
import { useConfig } from 'util/features';
import { defineModuleMap } from 'util/moduledata';
import type { TLabel } from 'util/stdtypes';
import { makeAssetUrl } from 'util/util';

import { HorizBox, Pad, PadBox } from './basics';
import { colorSurfaceSecondary, colorTextPrimary } from './color';
import { CommentConfig } from './comment';
import { RichText } from './richtext';
import { Heading, UtilityText } from './text';

const moduleHelp = defineModuleMap<boolean>('USER_GLOBAL', 'help', null);

export type WillHelpBeSeenArgs = { datastore: Datastore; helpKey: string };

export async function getWillHelpBeSeenAsync({ datastore, helpKey }: WillHelpBeSeenArgs) {
    return await datastore.getModuleObjectAsync(moduleHelp, helpKey);
}

export type HelpBubbleProps = {
    label?: TLabel;
    text?: string;
    titleLabel?: TLabel;
    titleText?: string;
    padTop?: number;
    above?: boolean;
    right?: boolean;
    wide?: boolean;
    pointer?: boolean;
    helpKey: string;
    condition: boolean;
};

export function HelpBubble({
    label,
    text,
    titleLabel,
    titleText,
    padTop = 4,
    above = false,
    right = false,
    wide = false,
    pointer = false,
    helpKey,
    condition,
}: HelpBubbleProps) {
    const s = HelpBubbleStyle;
    const datastore = useDatastore();
    const seenBefore = useModuleObject(moduleHelp, helpKey);

    if (!condition || seenBefore || seenBefore === undefined) return null;

    async function onClose() {
        await datastore.setModuleObjectAsync(moduleHelp, helpKey, true);
    }

    const hasTitle = titleLabel || titleText;

    return (
        <View>
            {pointer && (
                <View style={[s.pointer, { top: padTop }]}>
                    <UpTriangle />
                </View>
            )}
            <View style={[above ? s.aboveHover : s.belowHover, !above ? { top: pointer ? padTop + 10 : padTop } : null]}>
                <View style={[s.bubble, wide ? null : { maxWidth: 287 }, right ? { alignSelf: 'flex-end' } : null]}>
                    <HorizBox spread>
                        <View style={{ flex: 1 }}>
                            {hasTitle && (
                                <UtilityText
                                    color={colorSurfaceSecondary}
                                    label={titleLabel}
                                    text={titleText}
                                    type='small'
                                    weight='medium'
                                />
                            )}
                            {hasTitle && <Pad size={4} />}
                            <UtilityText color={colorSurfaceSecondary} label={label} text={text} type={hasTitle ? 'tiny' : 'small'} />
                        </View>
                        <Pad size={12} />
                        <Pressable onPress={onClose} testID={'close-' + helpKey}>
                            <CloseLarge color={colorSurfaceSecondary} />
                        </Pressable>
                    </HorizBox>
                </View>
            </View>
        </View>
    );
}
const HelpBubbleStyle = StyleSheet.create({
    pointer: {
        position: 'absolute',
        left: 20,
        top: 0,
    },
    bubble: {
        padding: 16,
        borderRadius: 8,
        backgroundColor: colorTextPrimary,
    },
    belowHover: {
        position: 'absolute',
        top: 10,
        left: 0,
        right: 0,
    },
    aboveHover: {
        position: 'absolute',
        bottom: 10,
        left: 0,
        right: 0,
    },
});

function UpTriangle() {
    return (
        <svg fill='none' height='17' viewBox='0 0 24 17' width='24' xmlns='http://www.w3.org/2000/svg'>
            <path
                d='M10.4202 1.0349C11.2209 0.0035318 12.7791 0.00353236 13.5798 1.0349L23.3515 13.6216C24.3716 14.9356 23.4352 16.8481 21.7717 16.8481L2.22827 16.8481C0.564795 16.8481 -0.37163 14.9356 0.648476 13.6216L10.4202 1.0349Z'
                fill='#0F0F0F'
            />
        </svg>
    );
}
